import React, { useState, useEffect, useRef } from "react";
import { CustomTable } from "../../components/Table";
import Article from "./Article";
import { FaSearch, FaTimes } from "react-icons/fa";
import NoData from "../../assets/no-data.png";
import {
  fetchJournals,
  fetchJournalOverview,
} from "../../services/apiService/journalApi";
import useToaster from "../../hooks/useToaster";
import dayjs from "dayjs";
import { PageSkeletons, Spinner } from "../../components/ui/Skeleton";
import { useSelector } from "react-redux";
import DataSyncError from "../../components/edgeCases/DataSyncError";

/**
 * @component JournalOverview
 * @description Displays a list of journals with their article counts and allows searching and viewing individual journal articles.
 * @param {Object} props
 * @param {string} props.active - The title to display at the top.
 * @param {Function} props.setLoading - Function to toggle global loading state.
 * @param {Function} props.setIsReload - Function to trigger a reload on data fetch failure.
 * @returns {JSX.Element}
 */
const JournalOverview = ({ active }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const [sampleData, setSampleData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const showToast = useToaster();
  const [loadingState, setLoadingState] = useState("spinning");
  const [isArticleLoading, setIsArticleLoading] = useState(false);
  let { userData, preferences, dataSyncTime } = useSelector(
    (state) => state.user
  );
  const [activeRowId, setActiveRowId] = useState(null);
  let [sortConfig, setSortConfig] = useState({
    key: "journalName",
    direction: "asc",
  });

  const searchTriggeredRef = useRef(false);
  const sortEffectRanRef = useRef(false);

  /**
   * @description Fetches journals and articles, enriches journal list with unique entries,
   * sets loading phases, and handles failures with fallback UI state.
   * @effect
   * @async
   */
  const fetchJournalsAndSetData = async () => {
    setIsLoading(true);
    // setLoading(true);
    const startTime = Date.now();

    try {
      const timeTaken = Date.now() - startTime;
      await JournalDataFetch();
      sortEffectRanRef.current = true;
      if (timeTaken < 2000) {
        setLoadingState("data");
      } else {
        setLoadingState("data");
      }
    } catch (err) {
      showToast(err.message || "Something went wrong. Please try again.");
    } finally {
      //   if (!success) setIsReload(true);
      setIsLoading(false);
      //   setLoading(false);
    }
  };

  let JournalDataFetch = async () => {
    try {
      const journalResponse = await fetchJournalOverview({
        id: userData.id,
        key: sortConfig.key,
        direction: sortConfig.direction,
        search: searchTerm.trim(),
      });
      if (journalResponse.success) {
        if (journalResponse?.data && journalResponse?.data?.data.length >= 1) {
          setSampleData(journalResponse.data.data);
        } else {
          setSampleData([]);
        }
      }
    } catch (err) {
      setSampleData([]);
      console.log("Error occurred while fetching journals");
    }
  };

  useEffect(() => {
    if (!userData?.id) return;
    fetchJournalsAndSetData();
  }, []);

  useEffect(() => {
    if (!sortEffectRanRef.current) return;
    fetchJournalsAndSetData();
  }, [userData?.id, preferences, sortConfig]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!searchTriggeredRef.current) return;
      if (searchTerm.trim().length > 2 || !searchTerm.trim().length) {
        fetchJournalsAndSetData();
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  /**
   * @description Enriches journals with article counts and prioritizes preferred journals.
   * @memo
   */

  /**
   * @description Filters journals based on the search term, returning all journals if the search term is too short.
   * @memo
   * @returns {Array} Filtered array of journals.
   */

  const tableSize = sampleData.length;

  /**
   * header for the table
   */
  const headers = [
    { key: "journalName", value: "JOURNAL" },
    { key: "articleCount", value: "NO. OF ARTICLES" },
  ];

  /**
   * @description Sets the selected journal to view its articles.
   * @param {Object} journal - The journal object to view.
   */
  const handleViewArticles = (journal) => {
    setIsArticleLoading(true);
    setSelectedJournal(journal);
  };

  /**
   * @description Renders a button to view articles for a journal row.
   * @param {Object} row - The journal row data.
   * @returns {JSX.Element} Button element for viewing articles.
   */
  const actions = (row) => (
    <button
      className="text-[#1C40C9] border-[#1C40C9] border-2 p-2 font-semibold text-[13px] rounded-lg hover:bg-[#B8C7FE]"
      onClick={() => handleViewArticles(row)}
    >
      View Articles
    </button>
  );

  /**
   * @description Updates the search term state when the search input changes.
   * @param {Object} e - The input change event.
   */
  const handleChange = (e) => {
    searchTriggeredRef.current = true;
    setSearchTerm(e.target.value);
  };

  /**
   * @description Clears the search term and removes input focus.
   */
  const handleClear = () => {
    setSearchTerm("");
    setIsLoading(true);
    setIsFocused(false);
  };

  /**
   * @description Resets the selected journal to return to the journal list view.
   */
  const handleBackToJournals = () => {
    setSelectedJournal(null);
    setActiveRowId(null);
  };

  return loadingState === "spinning" ? (
    <div className="flex items-center justify-center h-full">
      <Spinner />
    </div>
  ) : loadingState === "skeleton" ? (
    <PageSkeletons />
  ) : (
    <div
      className="flex flex-col h-full bg-white"
      style={{ fontFamily: "Source Sans Pro" }}
    >
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="p-6 flex flex-col h-full">
          {!selectedJournal ? (
            <>
              <div className="shrink-0 bg-white sticky top-0 z-10">
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-[#20282E] text-[23px] h-[32px] font-semibold">
                    {active} {tableSize > 0 && `(${tableSize})`}
                  </h1>
                  <span className="text-[11px] h-[14px] text-[#35424D] font-medium flex items-center">
                    <DataSyncError />
                    Last Sync: {dataSyncTime?.value}
                    <br></br>
                    {/* {dataSyncTime?.value ? "Snowflake data not available." : ""} */}
                  </span>
                </div>
                {/* {!isLoading && sampleData.length > 0 && ( */}
                <div className="relative w-[246px] mb-3">
                  <input
                    type="text"
                    className={`border rounded-[4px] w-full h-[32px] py-[4px] px-[12px] pr-[32px] text-sm transition-colors ${
                      isFocused
                        ? " focus:border-[#35424D] focus:border focus:outline-none"
                        : "border-[#C3C3C3]"
                    }`}
                    placeholder="Search"
                    maxLength={100}
                    value={searchTerm}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => !searchTerm && setIsFocused(false)}
                  />
                  {searchTerm ? (
                    <FaTimes
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-[#C3C3C3]"
                      onClick={handleClear}
                    />
                  ) : (
                    <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#C3C3C3]" />
                  )}
                </div>

                {/* )} */}
              </div>
              {searchTerm.length >= 3 && (
                <p className="mt-1 mb-4 text-sm text-gray-600">
                  Showing search results:{" "}
                  <span className="text-black italic">'{searchTerm}'</span>
                </p>
              )}
              <div
                className={`overflow-y-auto min-h-0 ${
                  sampleData.length > 0
                    ? " border border-[#DCDCDC] rounded-md"
                    : ""
                }`}
              >
                {isLoading && sampleData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[50vh]">
                    <Spinner />
                  </div>
                ) : sampleData.length === 0 && !searchTerm ? (
                  <div className="flex flex-col items-center justify-center mt-[10%]">
                    <img
                      src={NoData}
                      alt="No Data"
                      className="w-32 h-32 mb-4"
                    />
                    <h3 className="text-xl text-[#35424D] font-semibold mb-2">
                      {sampleData.length === 0
                        ? "No Journal information at the moment"
                        : "No journals match your search"}
                    </h3>
                  </div>
                ) : (
                  <div className="h-full">
                    <CustomTable
                      data={sampleData}
                      headers={headers}
                      actions={actions}
                      searchTerm={searchTerm.trim()}
                      setSearchTerm={setSearchTerm}
                      setLoading={setIsLoading}
                      setActiveRowId={setActiveRowId}
                      activeRowId={activeRowId}
                      sortConfig={sortConfig}
                      setSortConfig={setSortConfig}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <Article
              journal={selectedJournal}
              config={selectedJournal.fieldConfiguration}
              onBack={handleBackToJournals}
              setIsArticleLoading={setIsArticleLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JournalOverview;
